import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Password } from "primereact/password";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { useAddMutation, useLazyGetQuery } from "../../store/slices/apiSlice";
import { useSelector } from "react-redux";
import { ContextMenu } from "primereact/contextmenu";
import styles from "./RheologyCalculator.module.css";

export default function RheologyCalculator() {
  /*---toast---*/
  const cm = useRef(null);
  const toast = useRef(null);

  /*---auth---*/
  const [isAuthorized, setAuthorized] = useState(false);
  const [credentials, setCredentials] = useState("");

  const rheologyAccessEP = useSelector(
    (state) => state.endpoints.check_rheology_access
  );

  const [getAuthDebug] = useLazyGetQuery();

  const validateAuth = async (creds) => {
    console.log("Trying to validate auth");
    try {
      let response = await getAuthDebug({
        endpoint: rheologyAccessEP,
        credentials: creds,
      }).unwrap();
      console.log("response", response);
      return true;
    } catch (error) {
      // Неверный логин или пароль
      toast.current.show({
        severity: "error",
        summary: "Invalid login or password",
        life: 3000,
      });
      return false;
    }
  };

  // Проверка входа
  useEffect(() => {
    // Получаем данные авторизации из localStorage
    const creds = localStorage.getItem("credentials");

    if (creds) {
      validateAuth(creds).then((res) => {
        if (res) setAuthorized(true);
        else localStorage.removeItem("credentials");
      });
    } else {
      setAuthorized(false);
    }
  }, [credentials]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const auth = () => {
    const creds = `${username}:${password}`;
    setCredentials(creds);
    getAuthDebug({
      endpoint: rheologyAccessEP,
      credentials: creds,
    });
    validateAuth(creds).then((res) => {
      if (res) {
        setAuthorized(true);
      } else {
        setAuthorized(false);
      }
    });
  };

  /*---datatable-vars---*/
  const [temperature, setTemperature] = useState(22);
  const [density, setDensity] = useState(1.5);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [k1, setK1] = useState();
  const [k2, setK2] = useState();
  const [m, setM] = useState();
  const [n, setN] = useState();
  const [yp, setYP] = useState();
  const [pv, setPV] = useState();
  const [k, setK] = useState();
  const [model, setModel] = useState();

  /*---response-data---*/
  const [rheologicalProp, setRheologicalProp] = useState([]);

  /*---viscometer-dropdown---*/
  const viscometerGeometry = [
    // R1B1, R1B2, Вручную
    { name: "R1B1", code: "B1BobSleeve" },
    { name: "R1B2", code: "B2BobSleeve" },
    { name: "Manual", code: "Custom" },
  ];

  const [selectedViscometerGeometry, setSelectedViscometerGeometry] = useState(
    viscometerGeometry[0]
  );

  /*---rheo-logic---*/
  useEffect(() => {
    switch (selectedViscometerGeometry?.code) {
      case "B1BobSleeve": {
        setK1(1.705);
        setK2(0.5099);
        setInputDisabled(true);
        break;
      }
      case "B2BobSleeve": {
        setK1(0.378);
        setK2(1);
        setInputDisabled(true);
        break;
      }
      case "Custom": {
        setK1(null);
        setK2(null);
        setInputDisabled(false);
        break;
      }
      default:
        return;
    }
  }, [selectedViscometerGeometry]);

  /*---chart-datatable---*/
  const viscosemeterData = [
    {
      rotationSpeed: 600,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 300,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 200,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 100,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 60,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 30,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 6,
      viscometerIndicator: null,
    },
    {
      rotationSpeed: 3,
      viscometerIndicator: null,
    },
  ];
  const [chartDatatable, setChartDatatable] = useState(viscosemeterData);
  const [selectedChartData, setSelectedChartData] = useState();

  const onCellEditComplete = (e) => {
    let { rowData, newValue, field, originalEvent: event } = e;
    if (newValue != null) {
      rowData[field] = newValue;
    } else event.preventDefault();
  };

  /*---datatable-num-editor---*/
  const numEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        onChange={(e) => options.editorCallback(e.value)}
        useGrouping={false}
        maxFractionDigits={5}
        className="custom-input-small"
      />
    );
  };

  const modelTemplate = (model) => {
    // Ньютоновская, Степенная, Бингам пластик, Гершель-Бакли
    if (model === "Newtonian") return "Newtonian";
    if (model === "PowerLaw") return "Power Law";
    if (model === "BinghamPlastic") return "Bingham Plastic";
    if (model === "HerschelBulkley") return "Herschel-Bulkley";
  };

  /*---post-data---*/
  const rheologyEP = useSelector((state) => state.endpoints.rheology);
  const [addData] = useAddMutation();

  const calculateRheology = () => {
    let array = [...chartDatatable];
    const count = chartDatatable.filter(
      (item) => item.viscometerIndicator !== null
    ).length;

    if (
      !selectedViscometerGeometry?.code ||
      !density ||
      !temperature ||
      !k1 ||
      !k2 ||
      count < 4
    ) {
      // Заполнены не все поля ввода. Пожалуйста, введите необходимую информацию
      toast.current.show({
        severity: "error",
        summary: "Not all input fields are filled",
        detail: "Please enter the required information",
        life: 3000,
      });
    } else {
      let body = {
        density: density,
        temperature: temperature,
        rSpeed600: chartDatatable.find((item) => item.rotationSpeed === 600)
          .viscometerIndicator,
        rSpeed300: chartDatatable.find((item) => item.rotationSpeed === 300)
          .viscometerIndicator,
        rSpeed200: chartDatatable.find((item) => item.rotationSpeed === 200)
          .viscometerIndicator,
        rSpeed100: chartDatatable.find((item) => item.rotationSpeed === 100)
          .viscometerIndicator,
        rSpeed60: chartDatatable.find((item) => item.rotationSpeed === 60)
          .viscometerIndicator,
        rSpeed30: chartDatatable.find((item) => item.rotationSpeed === 30)
          .viscometerIndicator,
        rSpeed6: chartDatatable.find((item) => item.rotationSpeed === 6)
          .viscometerIndicator,
        rSpeed3: chartDatatable.find((item) => item.rotationSpeed === 3)
          .viscometerIndicator,
        k1: k1,
        k2: k2,
        rheometerType: selectedViscometerGeometry.code,
      };

      // Данные обрабатываются... Пожалуйста, подождите
      toast.current.show({
        severity: "info",
        summary: "Data is being processed...",
        detail: "Please wait",
        life: 60000,
        sticky: true,
      });

      addData({
        endpoint: rheologyEP,
        id: "",
        body: body,
        credentials: credentials,
      }).then((response) => {
        if (response?.data) {
          let _rheologicalProp = [...response?.data];
          setRheologicalProp(_rheologicalProp);
          toast.current.clear();
          // Успешно
          toast.current.show({
            severity: "success",
            summary: "Success",
            life: 1000,
          });
        } else {
          toast.current.clear();
          // Что-то пошло не так. Пожалуйста, попробуйте позже
          toast.current.show({
            severity: "error",
            summary: "Something went wrong",
            detail: "Please try again later",
            life: 3000,
          });
        }
      });
    }
  };

  const clearData = () => {
    setTemperature(22);
    setDensity(1.5);
    setInputDisabled();
    setSelectedViscometerGeometry(viscometerGeometry[0]);
    setK1();
    setK2();
    setM();
    setN();
    setYP();
    setPV();
    setK();
    setRheologicalProp([]);
    setChartDatatable(viscosemeterData);
    setSelectedChartData();
  };

  /*---cell-templates---*/
  const floatTemplate = (value, decimals = 3) => {
    return value.toFixed(decimals);
  };

  /*---chart---*/
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue("--text-color");
    const textColorSecondary = documentStyle.getPropertyValue(
      "--text-color-secondary"
    );

    let values = []
      .concat(
        chartDatatable
          .filter((x) => x.viscometerIndicator != null)
          .map((x) => ({
            x: x.rotationSpeed,
            y: x.viscometerIndicator,
          }))
      )
      .sort((a, b) => a.x - b.x);

    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

    const data = {
      datasets: [
        {
          // Показатель вискозиметра (°)
          label: "Viscometer reading (°)",
          data: values,
          fill: false,
          borderColor: documentStyle.getPropertyValue("--green-500"),
          showLine: true,
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 1,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          ticks: {
            color: textColorSecondary,
            stepSize: 100,
          },
          grid: {
            color: surfaceBorder,
          },
          // Скорость вращения (об/мин)
          title: {
            display: true,
            text: "Rotation speed (RPM)",
            color: textColor,
          },
        },
        y: {
          type: "linear",
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
          // Показатель вискозиметра (°)
          title: {
            display: true,
            text: "Viscometer reading (°)",
            color: textColor,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [rheologicalProp]);

  const copyToClipboard = () => {
    const dataToCopy = chartDatatable
      .filter((item) => item.viscometerIndicator !== null)
      .map((item) => `${item.rotationSpeed}:${item.viscometerIndicator}`)
      .join("\n");

    navigator.clipboard.writeText(dataToCopy);
    // Скопировано. Данные скопированы в буфер обмена
    toast.current.show({
      severity: "info",
      summary: "Copied",
      detail: "Data copied to clipboard",
      life: 3000,
    });
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const rows = text.split("\n");
      const newData = rows
        .map((row) => {
          const [rotationSpeed, viscometerIndicator] = row
            .split(":")
            .map(Number);
          return { rotationSpeed, viscometerIndicator };
        })
        .filter((item) => !isNaN(item.viscometerIndicator));

      setChartDatatable((prevData) =>
        prevData.map((item) => {
          const newItem = newData.find(
            (newItem) => newItem.rotationSpeed === item.rotationSpeed
          );
          return newItem
            ? { ...item, viscometerIndicator: newItem.viscometerIndicator }
            : item;
        })
      );

      // Вставлено. Данные вставлены из буфера обмена
      toast.current.show({
        severity: "info",
        summary: "Pasted",
        detail: "Data pasted from clipboard",
        life: 3000,
      });
    } catch (err) {
      // Ошибка. Не удалось вставить данные
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to paste data",
        life: 3000,
      });
    }
  };

  // Контекстное меню: Копировать, Вставить
  const contextMenuItems = [
    { label: "Copy", icon: "pi pi-copy", command: copyToClipboard },
    { label: "Paste", icon: "pi pi-pencil", command: pasteFromClipboard },
  ];

  const authView = (
    <div className={styles.auth}>
      <Toast ref={toast} position="bottom-center" />
      {/* Чтобы продолжить, пожалуйста, авторизируйтесь */}
      <p>To continue, please authorize</p>
      <div className={styles.auth_box}>
        {/* Логин */}
        <input
          id="username"
          value={username}
          placeholder="Login"
          onChange={(e) => setUsername(e.target.value)}
        />
        {/* Пароль */}
        <Password
          id="password"
          value={password}
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
          toggleMask
          feedback={false}
        />
        {/* Войти */}
        <Button className={styles.btn} onClick={auth}>
          Sign In
        </Button>
      </div>
    </div>
  );

  const calculatorView = (
    <div className={styles.container}>
      <div className={styles.row}>
        {/* Закомментированный блок с полями ввода */}
        {/* 
        <div className={styles.column}>
        <div className={styles.input_wrapper}>
          <label>Температура, °C</label>
          <InputNumber
            className="custom-input"
            inputId="temperature"
            useGrouping={false}
            maxFractionDigits={5}
            value={temperature}
            onValueChange={(e) => setTemperature(e.value)}
          />
        </div>
        <div className={styles.input_wrapper}>
          <label>
            Плотность жидкости, г/см<sup>3</sup>
          </label>
          <InputNumber
            className="custom-input"
            inputId="density"
            useGrouping={false}
            maxFractionDigits={5}
            value={density}
            onValueChange={(e) => setDensity(e.value)}
          />
        </div>
        <div className={styles.input_wrapper}>
          <label>Геометрия вискозиметра:</label>
          <Dropdown
            value={selectedViscometerGeometry}
            onChange={(e) => setSelectedViscometerGeometry(e.value)}
            options={viscometerGeometry}
            optionLabel="name"
            className="custom-dropdown"
          />
        </div>
        <div className={styles.input_wrapper}>
          <label>K1:</label>
          <InputNumber
            className="custom-input"
            inputId="k1"
            useGrouping={false}
            maxFractionDigits={5}
            value={k1}
            onValueChange={(e) => setK1(e.value)}
            disabled={inputDisabled}
            tooltip={
              inputDisabled
                ? "Для ввода значения выберете ручной режим вискозиметра"
                : ""
            }
          />
        </div>
        <div className={styles.input_wrapper}>
          <label>K2:</label>
          <InputNumber
            className="custom-input"
            inputId="k2"
            useGrouping={false}
            maxFractionDigits={5}
            value={k2}
            onValueChange={(e) => setK2(e.value)}
            disabled={inputDisabled}
            tooltip={
              inputDisabled
                ? "Для ввода значения выберете ручной режим вискозиметра"
                : ""
            }
          />
        </div>
      </div> 
        */}

        <div className="card">
          <Toast ref={toast} position="bottom-center" />

          <DataTable
            value={chartDatatable}
            className={styles.datatable_full_w}
            tableStyle={{ minWidth: "20rem" }}
            selectionMode="single"
            selection={selectedChartData}
            onSelectionChange={(e) => setSelectedChartData(e.value)}
            dataKey="rotationSpeed"
            editMode="cell"
            onContextMenu={(e) => cm.current.show(e.originalEvent)}
          >
            {/* Скорость вращения (об/мин) */}
            <Column field="rotationSpeed" header="Rotation speed (RPM)" />
            {/* Показатель вискозиметра (°) */}
            <Column
              field="viscometerIndicator"
              header="Viscometer reading (°)"
              editor={(options) => numEditor(options)}
              onCellEditComplete={onCellEditComplete}
            />
          </DataTable>
          <div className={styles.desktop_btn}>
            {/* Рассчитать */}
            <Button
              className={styles.btn}
              onClick={() => setTimeout(calculateRheology, 50)}
            >
              Calculate
            </Button>
          </div>
          <div className={styles.desktop_btn}>
            {/* Сбросить */}
            <button className="btn-outlined" onClick={clearData}>
              Reset
            </button>
          </div>
        </div>

        <div className={styles.mobile_btn}>
          {/* Рассчитать */}
          <Button
            className={styles.btn}
            onClick={() => setTimeout(calculateRheology, 200)}
          >
            Calculate
          </Button>
        </div>
      </div>

      <div className={styles.column_left_border}>
        <div className={styles.desktop_datatable}>
          <ContextMenu model={contextMenuItems} ref={cm} />
          <DataTable
            value={rheologicalProp}
            size="small"
            className={styles.datatable_full_w}
            selectionMode="single"
            dataKey="m"
          >
            <Column
              field="n"
              header="n"
              body={(row) => floatTemplate(row.n, 2)}
            />
            {/* YP (Па) */}
            <Column
              field="yp"
              header="YP (Pa)"
              body={(row) => floatTemplate(row.yp, 3)}
            />
            {/* PV (мПа*с) */}
            <Column
              field="pv"
              header="PV (mPa*s)"
              body={(row) => floatTemplate(row.pv, 2)}
            />
            {/* K (Па*с*n') */}
            <Column
              field="k"
              header="K (Pa*s*n')"
              body={(row) => floatTemplate(row.k, 5)}
            />
            {/* Модель */}
            <Column
              field="model"
              header="Model"
              body={(row) => modelTemplate(row.model)}
            />
          </DataTable>
        </div>

        <div className="card" style={{ width: "100%" }}>
          <Chart
            type="line"
            data={chartData}
            options={chartOptions}
            className={styles.chart}
          />
        </div>

        <div className={styles.mobile_btn}>
          {/* Сбросить */}
          <button className="btn-outlined" onClick={clearData}>
            Reset
          </button>
        </div>
      </div>
    </div>
  );

  return isAuthorized ? calculatorView : authView;
}
